/**
 * Wave 5 (#919 #940 Part A) — pure helpers behind "tapping 19 should show
 * those 19 dogs".
 *
 * The server's `/rollup` counts are a fold over its `items` (packages/core/
 * modules/booking/day-semantics.ts). This module is the CLIENT half of the
 * same contract: a tapped cell becomes a `view`, and `itemInView` selects,
 * from the same `items`, exactly the rows that cell counted — using the
 * flags the server already derived (`startsToday`, `endsToday`,
 * `staysTonight`, `listStatus`), never re-deriving dates on the client.
 * The two can only disagree if this file stops mirroring the fold, which
 * is what its unit test pins.
 *
 * No React, no I/O.
 */
import { capacityPoolParent } from '@ferro/core/modules/booking/capacity-groups.js';
import type { DayListStatus, DayRollupItem, RollupCell } from './dashboard-api.js';

/**
 * What the list is showing. The five rollup cells map onto the first five;
 * the snapshot header's four numbers map onto `arriving` / `onSite` /
 * `goingHome` / `overnight`; the filter rail adds the status views.
 */
export type DayView =
  | 'all'
  | 'expected'
  | 'checkIns'
  | 'checkOuts'
  | 'overnight'
  | 'arriving'
  | 'onSite'
  | 'goingHome'
  | 'unconfirmed'
  | 'checkedOut';

export const DAY_VIEWS: ReadonlyArray<{ key: DayView; label: string }> = [
  { key: 'all', label: 'Everything today' },
  { key: 'arriving', label: 'Not yet arrived' },
  { key: 'onSite', label: 'On site' },
  { key: 'goingHome', label: 'Going home' },
  { key: 'overnight', label: 'Overnight tonight' },
  { key: 'checkedOut', label: 'Checked out' },
  { key: 'unconfirmed', label: 'Unconfirmed / requested' },
];

export const ROLLUP_CELLS: ReadonlyArray<RollupCell> = [
  'expected',
  'checkIns',
  'checkOuts',
  'overnight',
  'total',
];

/** Column headings — Gingr's words, which Ted reads several times a day. */
export const CELL_LABEL: Record<RollupCell, string> = {
  expected: 'Expected',
  checkIns: 'Check-ins',
  checkOuts: 'Check-outs',
  overnight: 'Overnight',
  total: 'Total',
};

/** The same headings at 375 px, where five columns share one row. */
export const CELL_SHORT_LABEL: Record<RollupCell, string> = {
  expected: 'Exp.',
  checkIns: 'In',
  checkOuts: 'Out',
  overnight: 'Night',
  total: 'Total',
};

export function viewForCell(cell: RollupCell): DayView {
  return cell === 'total' ? 'all' : cell;
}

export const LIST_STATUS_LABEL: Record<DayListStatus, string> = {
  requested: 'Requested',
  unconfirmed: 'Unconfirmed',
  expected: 'Expected',
  checked_in: 'Checked in',
  going_home: 'Going home',
  checked_out: 'Checked out',
};

/** Structural tone per status — the text label always rides alongside (never color-only). */
export const LIST_STATUS_TONE: Record<DayListStatus, 'success' | 'pending' | 'neutral' | 'danger'> = {
  requested: 'pending',
  unconfirmed: 'pending',
  expected: 'neutral',
  checked_in: 'success',
  going_home: 'success',
  checked_out: 'neutral',
};

const ARRIVED = new Set(['checked_in', 'checked_out']);
const NOT_YET_ARRIVED: ReadonlySet<DayListStatus> = new Set(['requested', 'unconfirmed', 'expected']);

/** Mirror of the server's `dayRollupCells` + snapshot buckets, read off the row's flags. */
export function itemInView(item: DayRollupItem, view: DayView): boolean {
  switch (view) {
    case 'all':
      return true;
    case 'expected':
      return item.startsToday;
    case 'checkIns':
      return item.startsToday && ARRIVED.has(item.status);
    case 'checkOuts':
      return item.endsToday && item.status === 'checked_out';
    case 'overnight':
      return item.staysTonight;
    case 'arriving':
      return item.startsToday && NOT_YET_ARRIVED.has(item.listStatus);
    case 'onSite':
      return item.listStatus === 'checked_in' || item.listStatus === 'going_home';
    case 'goingHome':
      return item.listStatus === 'going_home';
    case 'unconfirmed':
      return item.listStatus === 'requested' || item.listStatus === 'unconfirmed';
    case 'checkedOut':
      return item.listStatus === 'checked_out';
    default:
      return true;
  }
}

export interface DayListFilter {
  serviceTypeId: string | null;
  view: DayView;
}

export function filterDayItems(
  items: ReadonlyArray<DayRollupItem>,
  filter: DayListFilter,
): DayRollupItem[] {
  return items.filter(
    (it) =>
      (filter.serviceTypeId === null || it.serviceTypeId === filter.serviceTypeId) &&
      itemInView(it, filter.view),
  );
}

/** Distinct dogs across the rows — the unit the rollup counts in. */
export function countDogs(items: ReadonlyArray<DayRollupItem>): number {
  const ids = new Set<string>();
  for (const it of items) for (const d of it.dogs) ids.add(d.id);
  return ids.size;
}

/**
 * The hero's per-service meter cards (Johnny, 09-23: "why are the cards so
 * big if all they have is the number?"). What each meter can say under its
 * number, in DOGS, read off the SAME page-owned `/rollup` items the snapshot
 * strip and the day list read — no second fetch, no re-derived dates.
 *
 * A row belongs to a meter by its capacity pool (half-day daycare counts on
 * the daycare meter, exactly as facility-status folds it). A service leg that
 * rides a visit on today's list is skipped, as the server's snapshot fold
 * skips it: Milo's nail trim is not a second grooming dog.
 */
export interface ServiceMeterFacts {
  arriving: number;
  goingHome: number;
  checkedOut: number;
  overnight: number;
  /** Every dog on today's list for this service, whatever its state. */
  total: number;
}

export function serviceMeterFacts(
  items: ReadonlyArray<DayRollupItem>,
  service: string,
): ServiceMeterFacts {
  const bookingIds = new Set(items.map((it) => it.bookingId));
  const mine = items.filter(
    (it) =>
      capacityPoolParent(it.serviceType) === service &&
      !(it.parentBookingId != null && bookingIds.has(it.parentBookingId)),
  );
  const dogsIn = (view: DayView): number =>
    countDogs(mine.filter((it) => itemInView(it, view)));
  return {
    arriving: dogsIn('arriving'),
    goingHome: dogsIn('goingHome'),
    checkedOut: dogsIn('checkedOut'),
    overnight: dogsIn('overnight'),
    total: countDogs(mine),
  };
}

/**
 * Ted (the Riggins example): "If a dog has both a facility service and an
 * appointment that day, I want both visible wherever that dog appears:
 * Riggins — Half-Day Daycare | Bath/Grooming." For each dog, the display
 * names of every OTHER service it has on the day, keyed by dog id, so a row
 * can trail its siblings without a second read.
 */
export function otherServicesByDog(
  items: ReadonlyArray<DayRollupItem>,
): Map<string, Array<{ bookingId: string; serviceDisplayName: string }>> {
  const byDog = new Map<string, Array<{ bookingId: string; serviceDisplayName: string }>>();
  for (const it of items) {
    for (const d of it.dogs) {
      const list = byDog.get(d.id) ?? [];
      list.push({ bookingId: it.bookingId, serviceDisplayName: it.serviceDisplayName });
      byDog.set(d.id, list);
    }
  }
  return byDog;
}

const VIEW_KEYS: ReadonlySet<string> = new Set(DAY_VIEWS.map((v) => v.key));

export function parseDayView(raw: string | null | undefined): DayView {
  return raw && VIEW_KEYS.has(raw) ? (raw as DayView) : 'all';
}

/** The `/calendar/day` URL for a date + optional filter — the one place the query shape lives. */
export function dayListHref(args: {
  date: string;
  serviceTypeId?: string | null;
  view?: DayView;
}): string {
  const params = new URLSearchParams({ date: args.date });
  if (args.serviceTypeId) params.set('service', args.serviceTypeId);
  if (args.view && args.view !== 'all') params.set('view', args.view);
  return `/calendar/day?${params.toString()}`;
}

// ── Dates and clocks (display only — the server owns the semantics) ────

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(raw: string | null | undefined): raw is string {
  return Boolean(raw && ISO_DATE.test(raw));
}

/** YYYY-MM-DD ± days, as a calendar date (no timezone arithmetic). */
export function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return next.toISOString().slice(0, 10);
}

/** "Mon, Sep 15" — noon-anchored so no timezone can move the day. */
export function formatDayLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** "Sep 15" */
export function formatShortDate(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/** "07:30" → "7:30 AM"; anything else passes through. */
export function formatClock(hhmm: string | null | undefined): string | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

/**
 * The time words on a row: an appointment's start, else the requested
 * drop-off → pick-up window, else (for a stay) its dates. Null when the
 * booking carries no time at all — the row then shows nothing rather than
 * inventing "8:00 AM" the way the legacy board did.
 */
export function timeWordsOf(item: DayRollupItem): string | null {
  const start = formatClock(item.startTime);
  if (start) return start;
  const drop = formatClock(item.dropOffTime);
  const pick = formatClock(item.pickUpTime);
  if (drop && pick) return `${drop} → ${pick}`;
  if (drop) return `Drop-off ${drop}`;
  if (pick) return `Pick-up ${pick}`;
  if (item.startDate && item.endDate && item.startDate !== item.endDate) {
    return `${formatShortDate(item.startDate)} → ${formatShortDate(item.endDate)}`;
  }
  return null;
}
